import { FiArrowLeft, FiMoreVertical } from "react-icons/fi";
import { useState } from "react";

interface Message {
  id: number;
  sender: string;
  title: string;
  description: string;
  timestamp: string;
  avatar?: string;
  isRead?: boolean;
  role?: string;
  fullMessage?: string;
}

interface MessagePopupProps {
  isOpen: boolean;
  message: Message | null;
  onClose: () => void;
}

export function MessagePopup({ isOpen, message, onClose }: MessagePopupProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");

  if (!isOpen || !message) return null;

  const handleSendReply = () => {
    if (replyText.trim()) {
      console.log("Sending reply:", replyText);
      setReplyText("");
      setIsReplying(false);
    }
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      "from-indigo-400 to-indigo-600",
      "from-blue-400 to-blue-600",
      "from-purple-400 to-purple-600",
      "from-green-400 to-green-600",
      "from-orange-400 to-orange-600",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-[500px] bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition"
          >
            <FiArrowLeft size={20} />
            <span className="text-sm font-medium">Back to inbox</span>
          </button>
          <button className="text-gray-400 hover:text-gray-600 transition">
            <FiMoreVertical size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Sender Info */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(
                  message.sender,
                )} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}
              >
                {message.sender.charAt(0)}
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {message.sender}
                    </h3>
                    <p className="text-sm text-gray-500">{message.role}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {message.timestamp}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Message Title and Content */}
          <div className="px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {message.title}
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
              {message.fullMessage}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          {!isReplying ? (
            <div className="flex gap-3">
              <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition">
                Close
              </button>
              <button
                onClick={() => setIsReplying(true)}
                className="flex-1 px-4 py-2 bg-indigo-700 text-white rounded-lg text-sm font-medium hover:bg-indigo-800 transition"
              >
                Reply
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setIsReplying(false);
                    setReplyText("");
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim()}
                  className="px-4 py-2 bg-indigo-700 text-white rounded-lg text-sm font-medium hover:bg-indigo-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
